import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  BudgetItem,
  BudgetItemSummary,
  BudgetMacroItemComplete,
  CostCenterSummary,
  MacroBudgetItemsService,
  ProjectArticle,
  ProjectSubtaskSummary,
} from '@libs/core/api';
import {
  animationFrameScheduler,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  from,
  identity,
  interval,
  map,
  Observable,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { ModalController } from '@ionic/angular';
import {
  AnimationsService,
  LoadingService,
  ToastService,
} from '../../services';
import { IonicBaseComponent } from '@front-end/core/ionic-hooks';
import { getValueOrError } from '@front-end/core/utils';
import { SubtaskSelectModalComponent } from '../budget-item-select-modal/budget-item-select-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export interface BudgetItemSearchResult {
  macroItem: BudgetMacroItemComplete;
  budgetItem: BudgetItemSummary;
}

@UntilDestroy()
@Component({
  selector: 'ui-budget-item-select',
  templateUrl: './budget-item-select.component.html',
  styleUrls: ['./budget-item-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SubtaskSelectComponent,
    },
  ],
})
export class SubtaskSelectComponent
  extends IonicBaseComponent
  implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input() placeholder?: string;
  @Input() project!: CostCenterSummary;
  @Input() projectTasks: BudgetMacroItemComplete[] = [];
  @Input() allowedSubtasks: ProjectSubtaskSummary[] = [];
  @Input() articleFormControl: FormControl<ProjectArticle | undefined | null> =
    new FormControl();
  @Input() searchable = false;

  selectedSubtask?: BudgetItem;

  displayText = '';

  touched = false;
  disabled = false;

  @ViewChild('searchInputEl', { read: ElementRef })
  searchInputEl?: ElementRef<HTMLElement>;

  @ViewChild('dropdownEl', { read: ElementRef })
  dropdownEl?: ElementRef<HTMLElement>;

  readonly searchControl = new FormControl('', { nonNullable: true });
  searchResults: BudgetItemSearchResult[] = [];
  showResults = false;
  dropdownStyle: Record<string, string> = {};

  private cachedProjectTasks?: BudgetMacroItemComplete[];
  private prefetchSubscription?: Subscription;

  constructor(
    private modalController: ModalController,
    private projectTasksService: MacroBudgetItemsService,
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private animationService: AnimationsService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.searchable) {
      return;
    }

    this.searchControl.valueChanges
      .pipe(
        tap((value) => {
          if (!value.trim()) {
            this.clearSelection();
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => this.updateSearchResults(value)),
        untilDestroyed(this)
      )
      .subscribe();

    // ion-content scrolls inside its shadow DOM, where scroll events don't reach
    // window: follow the input frame by frame while the dropdown is open.
    interval(0, animationFrameScheduler)
      .pipe(
        filter(() => this.showResults),
        tap(() => this.updateDropdownPosition()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    if (this.dropdownEl) {
      document.body.appendChild(this.dropdownEl.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.dropdownEl?.nativeElement.remove();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectTasks']) {
      this.updateDisplayText();
    }

    if (
      changes['searchable'] ||
      changes['project'] ||
      changes['projectTasks'] ||
      changes['allowedSubtasks']
    ) {
      this.prefetchProjectTasks();
    }
  }

  updateDisplayText(): void {
    if (!this.selectedSubtask) {
      this.displayText = '';

      if (this.searchable) {
        this.searchControl.setValue('', { emitEvent: false });
      }

      return;
    }

    if (
      this.selectedSubtask.projectArticles &&
      this.selectedSubtask.projectArticles.length > 0
    ) {
      const article = this.selectedSubtask.projectArticles[0];
      this.articleFormControl.setValue(article);

      this.displayText = this.translateService.instant(
        'pages.approvals.details.subtask.value',
        {
          article: article?.description,
          task: this.selectedSubtask?.macroItem?.name,
          subtask: this.selectedSubtask?.name,
        }
      );
    } else {
      this.displayText = this.selectedSubtask.name || '';
    }

    if (this.searchable) {
      this.searchControl.setValue(this.displayText, { emitEvent: false });
    }

    this.changeDetector.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
  onChange = (selectedSubtask: BudgetItemSummary) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(selectedSubtask: BudgetItemSummary) {
    this.selectedSubtask = selectedSubtask;
    this.updateDisplayText();
  }

  registerOnChange(onChange: never) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: never) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  openModal() {
    this.fetchProjectTasks()
      .pipe(
        switchMap((filteredProjectTasks) =>
          this.openModalWith(filteredProjectTasks, this.selectedSubtask)
        ),
        tap(({ data }) => {
          this.writeValue(data);
          this.onChange(data);
          this.changeDetector.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private clearSelection(): void {
    if (!this.selectedSubtask) {
      return;
    }

    this.writeValue(undefined as unknown as BudgetItemSummary);
    this.articleFormControl.setValue(null);
    this.onChange(null as unknown as BudgetItemSummary);
    this.changeDetector.markForCheck();
  }

  onSearchFocus(): void {
    if (!this.searchable) {
      return;
    }

    this.showResults = true;
    this.updateDropdownPosition();

    if (this.cachedProjectTasks) {
      this.updateSearchResults(this.searchControl.value);
      return;
    }

    // Prefetch still running (results show up when it lands) or failed (retry).
    if (!this.prefetchSubscription || this.prefetchSubscription.closed) {
      this.prefetchProjectTasks();
    }
  }

  onSearchBlur(): void {
    // Let a result's (mousedown) selection run before the list is hidden.
    setTimeout(() => {
      this.showResults = false;
      this.changeDetector.markForCheck();
    }, 150);
  }

  onResultSelected(result: BudgetItemSearchResult): void {
    this.showResults = false;

    const articles = result.budgetItem.projectArticles ?? [];

    if (articles.length === 1) {
      const budgetItem = this.toBudgetItem(
        result.macroItem,
        result.budgetItem,
        articles[0]
      );
      this.writeValue(budgetItem);
      this.onChange(budgetItem);
      this.changeDetector.markForCheck();
      return;
    }

    const filteredProjectTasks: BudgetMacroItemComplete[] = [
      { ...result.macroItem, budgetItems: [result.budgetItem] },
    ];

    this.openModalWith(filteredProjectTasks, result.budgetItem, true)
      .pipe(
        tap(({ data }) => {
          this.writeValue(data);
          this.onChange(data);
          this.changeDetector.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  // Loads the items in background as soon as the inputs are known, so the
  // search works from the first focus. Silent: no loading overlay, which would
  // steal the input's focus.
  private prefetchProjectTasks(): void {
    this.prefetchSubscription?.unsubscribe();
    this.cachedProjectTasks = undefined;

    if (!this.searchable || !this.project?.id) {
      return;
    }

    this.prefetchSubscription = this.fetchProjectTasks(true)
      .pipe(
        tap((projectTasks) => {
          this.cachedProjectTasks = projectTasks;
          this.updateSearchResults(this.searchControl.value);
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private updateDropdownPosition(): void {
    const inputElement = this.searchInputEl?.nativeElement;

    if (!inputElement) {
      return;
    }

    const rect = inputElement.getBoundingClientRect();
    const top = `${rect.bottom}px`;
    const left = `${rect.left}px`;
    const width = `${rect.width}px`;

    if (
      this.dropdownStyle['top'] === top &&
      this.dropdownStyle['left'] === left &&
      this.dropdownStyle['width'] === width
    ) {
      return;
    }

    this.dropdownStyle = { position: 'fixed', top, left, width };
    this.changeDetector.markForCheck();
  }

  private updateSearchResults(query: string): void {
    const normalizedQuery = (query ?? '').trim().toLowerCase();
    const projectTasks = this.cachedProjectTasks ?? [];
    const searchByCode = !!this.project?.showBudgetItemCodesInStp;

    if (!normalizedQuery) {
      this.searchResults = [];
      this.changeDetector.markForCheck();
      return;
    }

    this.searchResults = projectTasks.flatMap((macroItem) =>
      (macroItem.budgetItems ?? [])
        .filter((budgetItem) =>
          this.matchesQuery(
            macroItem,
            budgetItem,
            normalizedQuery,
            searchByCode
          )
        )
        .map((budgetItem) => ({ macroItem, budgetItem }))
    );

    this.changeDetector.markForCheck();
  }

  private matchesQuery(
    macroItem: BudgetMacroItemComplete,
    budgetItem: BudgetItemSummary,
    normalizedQuery: string,
    searchByCode: boolean
  ): boolean {
    const matchesKeyword =
      (macroItem.name ?? '').toLowerCase().includes(normalizedQuery) ||
      (budgetItem.name ?? '').toLowerCase().includes(normalizedQuery);

    if (matchesKeyword) {
      return true;
    }

    return (
      searchByCode &&
      (budgetItem.code ?? '').toLowerCase().includes(normalizedQuery)
    );
  }

  private toBudgetItem(
    macroItem: BudgetMacroItemComplete,
    budgetItem: BudgetItemSummary,
    article: ProjectArticle
  ): BudgetItem {
    return {
      id: budgetItem.id,
      name: budgetItem.name,
      code: budgetItem.code,
      totalBudget: budgetItem.totalBudget,
      residualBudget: budgetItem.residualBudget,
      usedBudget: budgetItem.usedBudget,
      macroItem,
      projectArticles: [article],
    };
  }

  private fetchProjectTasks(
    silent = false
  ): Observable<BudgetMacroItemComplete[]> {
    return this.projectTasksService
      .macroBudgetItemList({
        id: getValueOrError(this.project.id),
        $macroItems:
          this.projectTasks.length > 0
            ? this.projectTasks.map((projectTask) =>
                getValueOrError(projectTask.id)
              )
            : undefined,
      })
      .pipe(
        first(),
        silent ? identity : this.loadingService.withLoading(),
        this.toastService.catchErrorWithToast(),
        map((projectTasks) => {
          if (this.allowedSubtasks.length > 0) {
            const allowedSubtaskIds = new Set(
              this.allowedSubtasks.map((subtask) => subtask.id)
            );

            return projectTasks
              .map((task) => {
                if (task.budgetItems && task.budgetItems.length > 0) {
                  const filteredItems = task.budgetItems.filter((item) =>
                    allowedSubtaskIds.has(item.id)
                  );

                  return {
                    ...task,
                    budgetItems: filteredItems,
                  };
                }

                return task;
              })
              .filter(
                (task) => task.budgetItems && task.budgetItems.length > 0
              );
          }

          return projectTasks;
        })
      );
  }

  private openModalWith(
    projectTasks: BudgetMacroItemComplete[],
    selected?: BudgetItemSummary,
    startAtArticles = false
  ) {
    return from(
      this.modalController.create({
        component: SubtaskSelectModalComponent,
        mode: 'md',
        componentProps: {
          projectTasks,
          selected,
          startAtArticles,
        },
        enterAnimation: this.animationService.slideLeftToRight,
        leaveAnimation: this.animationService.slideRightToLeft,
      })
    ).pipe(
      switchMap((modal) => from(modal.present()).pipe(map(() => modal))),
      switchMap((modal) => from(modal.onWillDismiss())),
      filter(({ role }) => role === 'confirm')
    );
  }
}
